import React from "react";
import {
  FaBrain,
  FaRegCheckCircle,
  FaUsers,
  FaChartBar,
  FaComments,
  FaUserTie,
  FaArrowRight,
  FaClock,
} from "react-icons/fa";
import {
  MdOutlineWork,
  MdOutlineInsights,
  MdOutlineDescription,
  MdOutlineThumbUp,
} from "react-icons/md";

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <section className="relative py-24 px-4 overflow-hidden bg-gray-900 text-white">
        {/* Animated gradient background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-purple-900 via-indigo-900 to-gray-900 animate-gradient-shift"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[length:20px_20px]"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/30 to-transparent"></div>
        </div>

        {/* Floating particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-white/10"
              style={{
                width: `${Math.random() * 6 + 2}px`,
                height: `${Math.random() * 6 + 2}px`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animation: `float ${Math.random() * 10 + 10}s linear infinite`,
                animationDelay: `${Math.random() * 5}s`,
              }}
            />
          ))}
        </div>

        {/* Glowing accent elements */}
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-purple-600 rounded-full filter blur-[100px] opacity-20"></div>
        <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-indigo-600 rounded-full filter blur-[100px] opacity-20"></div>

        <div className="relative max-w-6xl mx-auto z-10">
          <div className="max-w-3xl mx-auto text-center">
            {/* Animated icon with gradient border */}
            <div className="inline-flex justify-center items-center mb-8">
              <div className="relative">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 p-1 animate-gradient-rotate">
                  <div className="absolute inset-0 rounded-xl bg-gray-900 blur-sm"></div>
                </div>
                <div className="relative flex justify-center items-center  rounded-xl bg-gray-900/80 backdrop-blur-sm border border-white/10">
                  <FaBrain
                    className="text-4xl  bg-clip-text text-purple-400 p-4"
                    size={150}
                  />
                </div>
              </div>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-br from-white to-purple-200 bg-clip-text text-transparent">
              Discover What Makes You Thrive at Work
            </h1>

            <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
              This is not just a test — it's a personal insight tool designed to
              help you understand how you think, work, and grow best. In just 45
              minutes, you'll unlock clarity that helps you do your best work,
              in the right environment.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-8">
            Why Take This Assessment?
          </h2>
          <p className="text-xl font-bold text-center text-gray-900 mb-4">
            Your Manager / Recruiter will gain insights that can help them
          </p>
          <div className="flex flex-col items-center">
            {/* First row - 3 cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-5xl mb-6">
              {[
                {
                  icon: <FaUsers size={28} className="text-purple-500" />,
                  title: "Understand your natural working style",
                },
                {
                  icon: <FaChartBar size={28} className="text-purple-500" />,
                  title: "Discover where you add the most value to a team",
                },
                {
                  icon: <FaComments size={28} className="text-purple-500" />,
                  title: "Learn how you can communicate and collaborate better",
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 hover:border-purple-100 hover:-translate-y-1"
                >
                  <div className="mb-5 p-3 w-12 h-12 flex items-center justify-center bg-purple-50 rounded-lg">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 leading-snug">
                    {item.title}
                  </h3>
                </div>
              ))}
            </div>

            {/* Second row - 2 centered cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-3xl">
              {[
                {
                  icon: <MdOutlineWork size={28} className="text-purple-500" />,
                  title:
                    "Get clarity on roles and responsibilities that suit you",
                },
                {
                  icon: <FaUserTie size={28} className="text-purple-500" />,
                  title:
                    "Feel more confident in how you show up professionally",
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 hover:border-purple-100 hover:-translate-y-1"
                >
                  <div className="mb-5 p-3 w-12 h-12 flex items-center justify-center bg-purple-50 rounded-lg">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 leading-snug">
                    {item.title}
                  </h3>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            What to Expect
          </h2>
          <div className="space-y-6">
            {[
              {
                icon: <FaClock className="text-purple-600" />,
                text: "It's fast — just 45 minutes",
              },
              {
                icon: <FaRegCheckCircle className="text-purple-600" />,
                text: "There are no right or wrong answers",
              },
              {
                icon: <MdOutlineDescription className="text-purple-600" />,
                text: "You’ll answer based on real-life, everyday situations",
              },
              {
                icon: <MdOutlineInsights className="text-purple-600" />,
                text: "At the end, your Manager / Recruiter will receive a detailed assessment report",
              },
            ].map((item, index) => (
              <div key={index} className="flex items-start">
                <div className="flex-shrink-0 mt-1">{item.icon}</div>
                <p className="ml-3 text-lg text-gray-700">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Designed to Help You Succeed
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            This assessment isn’t about labeling you — it’s about understanding
            how to help you grow. Whether you’re applying for a role, exploring
            leadership, or just curious about your strengths, this tool gives
            you practical, easy-to-use insight to move forward with confidence.
          </p>
          <div className="bg-purple-50 p-6 rounded-xl inline-block">
            <FaClock className="h-12 w-12 text-purple-600 mx-auto mb-4" />
            <p className="text-lg text-gray-700">
              Takes just <span className="font-bold">45 minutes</span> to
              complete
            </p>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            What Happens After
          </h2>
          <p className="text-xl font-bold text-gray-900 mb-6 text-left">
            Once you complete the assessment:
          </p>
          <div className="space-y-6">
            {[
              {
                icon: (
                  <MdOutlineDescription size={32} className="text-purple-600" />
                ),
                text: "Your Manager / Recruiter will receive a detailed assessment report",
              },
              {
                icon: (
                  <MdOutlineInsights size={32} className="text-purple-600" />
                ),
                text: "They will gain practical insights on your natural strengths",
              },
              {
                icon: <FaChartBar size={32} className="text-purple-600" />,
                text: "Your results may help guide conversations about role fit, growth, and performance",
              },
            ].map((item, index) => (
              <div key={index} className="flex items-start">
                <div className="flex-shrink-0 mt-1">{item.icon}</div>
                <p className="ml-3 text-lg text-gray-700">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Final Reassurance
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            There are no wrong answers — just real insights. This is about you —
            and helping you grow in the direction that feels right.
          </p>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
